import { resolveLayout } from "@/helpers/bioLayout";

export const MAX_TESTIMONIALS = 3;
export const BIO_LIMIT = 2500;

const text = (value) => String(value || "").trim();

const parseLayout = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

export const draftFrom = (details) => {
  const testimonials = (Array.isArray(details?.clientTestimonial) ? details.clientTestimonial : [])
    .map(text)
    .filter(Boolean);
  return {
    Img: details?.Img || "",
    logoUrl: details?.logoUrl || "",
    Bio: details?.Bio || "",
    testimonials: testimonials.length ? testimonials : [""],
    websiteUrl: details?.websiteUrl || "",
    facebookUrl: details?.facebookUrl || "",
    instagramUrl: details?.instagramUrl || "",
    youtubeUrl: details?.youtubeUrl || "",
    bioLayout: resolveLayout(parseLayout(details?.BioLayout)),
  };
};

export const BIO_STEPS = [
  {
    key: "photo",
    done: (d) => Boolean(text(d.Img)),
    label: "Add a photo",
    why: "Buyers reach out to a face they can see.",
  },
  {
    key: "bio",
    done: (d) => text(d.Bio).length >= 40,
    label: "Write a short bio",
    why: "Say where you work and who you help, in two or three sentences.",
  },
  {
    key: "testimonial",
    done: (d) => d.testimonials.some((item) => text(item)),
    label: "Add a client testimonial",
    why: "It is the first thing buyers read under your name.",
  },
  {
    key: "logo",
    done: (d) => Boolean(text(d.logoUrl)),
    label: "Add your brokerage logo",
    why: "Buyers see who you work with at a glance.",
  },
  {
    key: "links",
    done: (d) => [d.websiteUrl, d.facebookUrl, d.instagramUrl].some((item) => text(item)),
    label: "Link your website or social profiles",
    why: "Buyers can see more of your work before they reach out.",
  },
  {
    key: "video",
    done: (d) => Boolean(text(d.youtubeUrl)),
    label: "Add a YouTube video",
    why: "A home tour or a short intro lets buyers hear from you.",
  },
];

export const midSentence = (label) => {
  const value = String(label || "");
  return value.charAt(0).toLowerCase() + value.slice(1);
};

export const bioProgress = (draft) => {
  const done = BIO_STEPS.filter((step) => step.done(draft));
  const next = BIO_STEPS.find((step) => !step.done(draft)) || null;
  return { done: done.length, total: BIO_STEPS.length, next };
};

export const MISSING_DETAIL_LABELS = {
  FirstName: "first name",
  LastName: "last name",
  Company: "brokerage",
  Location: "location",
  LicenseNo: "license number",
};

const fromRoster = (roster, key) => (roster?.linked ? text(roster[key]) : "");

export const detailRows = (details, roster) => [
  ["Name", [details?.FirstName, details?.LastName].filter(Boolean).join(" ")],
  ["Brokerage", fromRoster(roster, "brokerage") || text(details?.Company)],
  ["License", fromRoster(roster, "license") || text(details?.LicenseNo)],
  ["Phone", text(details?.Phone)],
  ["Location", text(details?.Location)],
];

export const missingDetails = (details) =>
  Object.keys(MISSING_DETAIL_LABELS).filter((key) => !text(details?.[key]));

export const normalizeUrl = (value) => {
  const url = text(value);
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export const isValidUrl = (value) => {
  const url = text(value);
  if (!url) return true;
  try {
    const parsed = new URL(normalizeUrl(url));
    return parsed.hostname.includes(".");
  } catch (error) {
    return false;
  }
};

export const isYoutubeUrl = (value) => {
  const url = text(value);
  if (!url) return true;
  return /^(https?:\/\/)?([\w-]+\.)*(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]+/i.test(url);
};

export const draftErrors = (draft) => {
  const errors = {};
  ["websiteUrl", "facebookUrl", "instagramUrl"].forEach((key) => {
    if (!isValidUrl(draft[key])) errors[key] = "Enter a full web address, like westmarkrealtors.com";
  });
  if (!isYoutubeUrl(draft.youtubeUrl)) errors.youtubeUrl = "Paste a YouTube video link";
  if (text(draft.Bio).length > BIO_LIMIT) errors.Bio = `Keep your bio under ${BIO_LIMIT.toLocaleString("en-US")} characters`;
  return errors;
};

export const isDirty = (draft, details) => JSON.stringify(draft) !== JSON.stringify(draftFrom(details));

export const payloadFrom = (details, draft) => ({
  FirstName: details?.FirstName || "",
  LastName: details?.LastName || "",
  Company: details?.Company || "",
  Location: details?.Location || "",
  LicenseNo: details?.LicenseNo || "",
  Phone: details?.Phone || "",
  Title: Number(details?.Agent_Title) || 1,
  About: draft.Bio,
  Img: draft.Img,
  logoUrl: draft.logoUrl,
  clientTestimonial: draft.testimonials.map(text).filter(Boolean),
  websiteUrl: normalizeUrl(draft.websiteUrl),
  facebookUrl: normalizeUrl(draft.facebookUrl),
  instagramUrl: normalizeUrl(draft.instagramUrl),
  youtubeUrl: text(draft.youtubeUrl),
  bioLayout: draft.bioLayout,
});

export const previewAgent = (details, draft, asPro, profile) => ({
  activeWantAds: profile?.activeWantAds || [],
  activeWantAdCount: profile?.activeWantAdCount || 0,
  history: profile?.history || null,
  disclosure: profile?.disclosure,
  FirstName: details?.FirstName,
  LastName: details?.LastName,
  Company: details?.Company,
  licenseNo: details?.LicenseNo,
  Phone: details?.Phone,
  slug: details?.slug,
  Img: draft.Img,
  logoUrl: draft.logoUrl,
  Bio: draft.Bio,
  clientTestimonial: draft.testimonials.map(text).filter(Boolean),
  websiteUrl: normalizeUrl(draft.websiteUrl),
  facebookUrl: normalizeUrl(draft.facebookUrl),
  instagramUrl: normalizeUrl(draft.instagramUrl),
  youtubeUrl: text(draft.youtubeUrl),
  isPremiumAgent: asPro,
});
